import {
  type EpocusBillingStatus,
  RXDB_EPOCUS_BILLING_STATUS_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-billing-status";
import { Box, styled, Typography } from "@material-ui/core";
import { useRxData } from "rxdb-hooks";
import {
  EPOCUS_AMBER,
  EPOCUS_AMBER_BG,
  EPOCUS_AMBER_BORDER,
  EPOCUS_GREEN,
  EPOCUS_GREEN_BG,
  EPOCUS_GREEN_BORDER,
  EPOCUS_NEUTRAL_BG,
  EPOCUS_NEUTRAL_BORDER,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../epocus-palette";
import {
  BILLING_HOLD_REASON_HINTS,
  BILLING_HOLD_REASON_LABELS,
  BILLING_MISSING_ITEM_LABELS,
  BILLING_STATE_LABELS,
} from "./billing-status-labels";

const NoteRoot = styled(Box)({
  borderRadius: 10,
  padding: "10px 14px",
  borderWidth: 1,
  borderStyle: "solid",
});

const TONES = {
  ready: {
    backgroundColor: EPOCUS_GREEN_BG,
    borderColor: EPOCUS_GREEN_BORDER,
    color: EPOCUS_GREEN,
  },
  incomplete: {
    backgroundColor: EPOCUS_AMBER_BG,
    borderColor: EPOCUS_AMBER_BORDER,
    color: EPOCUS_AMBER,
  },
  neutral: {
    backgroundColor: EPOCUS_NEUTRAL_BG,
    borderColor: EPOCUS_NEUTRAL_BORDER,
    color: EPOCUS_TEXT_SECONDARY,
  },
} as const;

export const useExamBillingStatus = (
  examId: string | undefined,
): EpocusBillingStatus | null => {
  const { result } = useRxData<EpocusBillingStatus>(
    RXDB_EPOCUS_BILLING_STATUS_COLLECTION_NAME,
    (collection) => collection.findOne({ selector: { examId: examId ?? "" } }),
  );
  return (
    (result?.[0]?.toMutableJSON() as EpocusBillingStatus | undefined) ?? null
  );
};

export const BillingStatusNote: FC<{ status: EpocusBillingStatus }> = ({
  status,
}) => {
  const tone =
    status.status === "READY_TO_BILL" || status.status === "EXPORTED"
      ? TONES.ready
      : status.status === "INCOMPLETE" || status.holdReason
        ? TONES.incomplete
        : TONES.neutral;

  return (
    <NoteRoot style={tone} role="status" aria-label="Billing status">
      <Typography style={{ fontSize: rem(13), fontWeight: 700 }}>
        Billing: {BILLING_STATE_LABELS[status.status]}
        {status.holdReason
          ? ` · ${BILLING_HOLD_REASON_LABELS[status.holdReason]}`
          : ""}
        {status.amendedAfterExportAt ? " · amended after export" : ""}
      </Typography>
      {status.holdReason && (
        <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_PRIMARY }}>
          {BILLING_HOLD_REASON_HINTS[status.holdReason]}
        </Typography>
      )}
      {status.missingItems.length > 0 && (
        <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
          {status.missingItems.map((item) => (
            <li
              key={item}
              style={{ fontSize: rem(13), color: EPOCUS_TEXT_PRIMARY }}
            >
              {BILLING_MISSING_ITEM_LABELS[item]}
            </li>
          ))}
        </ul>
      )}
    </NoteRoot>
  );
};
